import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {toast} from 'react-toastify';
import {
  Button,
  Dropdown,
  DropdownProps,
  Form,
  Header,
  Input,
  Modal,
  SemanticCOLORS,
  SemanticSIZES,
} from 'semantic-ui-react';
import config, {envIsCloudOnprem, envIsLocal} from '../config';
import {
  OrgType,
  useCreateTeamMutation,
  useEntityQuery,
  useUserOrganizationsQuery,
} from '../generated/graphql';
import {useViewer} from '../state/viewer/hooks';
import {
  doNotRetryContext,
  extractErrorMessageFromApolloError,
  propagateErrorsContext,
} from '../util/errors';
import {navigateTo} from '../util/history';
import {TargetBlank} from '../util/links';
import {userFlags} from '../util/permissions';
import makeComp from '../util/profiler';
import {slugFormat} from '../util/text';
import * as urls from '../util/urls';
import * as S from './CreateTeamModal.styles';
import WBModal from './WBModal';

const TEAM_LIMIT_ERROR_MSG =
  'organization does not have any private teams left';

interface CreateTeamButtonProps {
  size?: SemanticSIZES;
  color?: SemanticCOLORS;
  readonly?: boolean;
  renderButton?(onClick: () => void): JSX.Element;
  onCreate(teamName?: string): void;
}

interface CreateTeamModalContentProps extends CreateTeamButtonProps {
  orgOptions: Array<{text: string; value: string}>;
  teamAdminUsername?: string;
  open?: boolean;
  trigger?: React.ReactNode;
  onClose?(): void;
  onClick?(event: React.MouseEvent<HTMLButtonElement>): void;
}

export const CreateTeamModal: FunctionComponent<CreateTeamModalContentProps> =
  makeComp(
    props => {
      const {orgOptions} = props;
      const viewer = useViewer();
      const [teamName, setTeamName] = useState('');
      const [selectedOrganization, setSelectedOrganization] = useState<
        string | null
      >(null);
      const [selectedOrgName, setSelectedOrgName] = useState<string | null>(
        null
      );
      const [teamNameIsAvailable, setTeamNameIsAvailable] = useState<
        boolean | null
      >(null);
      const [hitTeamLimit, setHitTeamLimit] = useState(false);

      // Set the first org as the default selection when it's available.
      useEffect(() => {
        if (orgOptions.length > 0) {
          setSelectedOrganization(orgOptions[0].value);
          setSelectedOrgName(orgOptions[0].text);
        }
      }, [orgOptions]);

      const handleTeamNameChange = useCallback(
        (e: any, data: any) => {
          setTeamName(slugFormat(data.value));
          setTeamNameIsAvailable(null);
        },
        [setTeamName, setTeamNameIsAvailable]
      );

      const handleOrganizationChange = useCallback(
        (_, data: DropdownProps) => {
          setSelectedOrganization(data.value as string);
          setSelectedOrgName(data.text as string);
        },
        [setSelectedOrganization, setSelectedOrgName]
      );

      const handleCompletedEntityQuery = useCallback(
        (queryData: any) => {
          if (queryData.entity.name !== teamName) {
            // We've edited the team name since this query was sent.
            return;
          }

          setTeamNameIsAvailable(queryData.entity.available);
        },
        [teamName, setTeamNameIsAvailable]
      );

      const isSubmitDisabled = useMemo(() => {
        if (teamName.length < 3) {
          return true;
        }
        if (teamNameIsAvailable !== true) {
          return true;
        }
        return false;
      }, [teamName, teamNameIsAvailable]);

      const teamNameIcon = useMemo(
        () => ({
          name: (isSubmitDisabled ? 'stop' : 'check') + ' circle outline',
          color: isSubmitDisabled ? 'red' : 'green',
          size: 'small',
        }),
        [isSubmitDisabled]
      );

      const entityQuery = useEntityQuery({
        fetchPolicy: 'network-only',
        variables: {name: teamName},
        skip: teamName === '',
        onCompleted: handleCompletedEntityQuery,
      });

      const [createTeam] = useCreateTeamMutation({
        onCompleted: () => props.onCreate(teamName),
        context: {...propagateErrorsContext(), ...doNotRetryContext()},
      });

      const createTeamCallback = useCallback(async () => {
        window.analytics?.track('Team Submitted', {
          organizationName: selectedOrgName,
          entityName: teamName,
          location: 'create team modal',
        });
        try {
          const variables: {
            teamName: string;
            organizationId?: string;
            teamAdminUserName?: string;
          } = {
            teamName,
          };
          if (selectedOrganization != null && selectedOrganization !== 'none') {
            variables.organizationId = selectedOrganization;
          }
          if (props.teamAdminUsername != null) {
            variables.teamAdminUserName = props.teamAdminUsername;
          }
          await createTeam({
            variables,
          });
          props.onClose?.();
        } catch (err) {
          const errMsg = extractErrorMessageFromApolloError(err);
          let analyticsMsg = '';
          if (errMsg === TEAM_LIMIT_ERROR_MSG) {
            setHitTeamLimit(true);
            analyticsMsg = `"contact sales" modal`;
          } else {
            toast('Permission denied, please contact support');
            analyticsMsg = `"permission denied, contact support" message`;
          }
          window.analytics?.track(`Create Team Error Viewed`, {
            organizationName: selectedOrgName,
            location: 'create team modal',
            error: analyticsMsg,
          });
        }
      }, [
        createTeam,
        props.onClose,
        props.teamAdminUsername,
        selectedOrganization,
        teamName,
        selectedOrgName,
      ]);

      const upgradePlanName = 'Enterprise Plan';
      const teamLimitMessage = `Your current organization plan ${
        selectedOrgName != null ? `for ${selectedOrgName}` : ''
      } only allows you to create one team. Please contact sales to upgrade to the ${upgradePlanName} and unlock multiple teams.`;

      const isOnPrem = config.ENVIRONMENT_IS_PRIVATE;

      const loading = viewer == null || entityQuery.loading;

      const closeModal = useCallback(() => {
        props.onClose?.();
        setHitTeamLimit(false);
      }, [props.onClose, setHitTeamLimit]);

      if (orgOptions.length === 0) {
        const message = envIsLocal ? (
          <>
            Teams functionality requires a valid license. Contact{' '}
            <a href="mailto:sales@wandb.com">sales@wandb.com</a> to get started.
          </>
        ) : (
          <>It's completely free to try, get started today!</>
        );
        const modalAction = envIsLocal
          ? undefined
          : {
              content: 'Create team',
              onClick: () => {
                window.analytics?.track('Create Team Started', {
                  location: 'create team modal',
                });
                navigateTo({pathname: '/create-team'});
              },
            };
        return (
          <WBModal
            trigger={props.trigger}
            open={props.open}
            onClose={closeModal}
            onClick={props.onClick}
            header="Collaborative Teams"
            primaryAction={modalAction}>
            Collaborate on ML projects in a central dashboard with W&B Teams.{' '}
            {message}
          </WBModal>
        );
      }

      return (
        <Modal
          open={props.open}
          onClose={closeModal}
          onClick={props.onClick}
          trigger={props.trigger}>
          <Header>
            {hitTeamLimit ? 'Upgrade Plan' : 'Choose your team name'}
          </Header>
          <Modal.Content>
            {hitTeamLimit ? (
              teamLimitMessage
            ) : (
              <Form size="large">
                {!isOnPrem && (
                  <Form.Field inline>
                    <label>Organization</label>
                    <Dropdown
                      data-test="choose-org-dropdown"
                      options={orgOptions}
                      value={selectedOrganization ?? ''}
                      direction="left"
                      onChange={handleOrganizationChange}
                      loading={loading}
                    />
                  </Form.Field>
                )}
                <Form.Field inline>
                  <label>Team Name</label>
                  <Input
                    data-test="team-name-input"
                    name="defaultEntity"
                    placeholder="Team Name"
                    loading={loading}
                    value={teamName}
                    onChange={handleTeamNameChange}
                    icon={teamNameIcon}
                  />
                </Form.Field>
                <S.HelperText>
                  Choose carefully! You will not be able to change the team name
                  later.
                </S.HelperText>
              </Form>
            )}
          </Modal.Content>
          <Modal.Actions>
            {props.onClose && <Button content="Cancel" onClick={closeModal} />}
            {hitTeamLimit ? (
              <TargetBlank
                href={urls.contactSales()}
                onClick={() =>
                  window.analytics?.track('Contact Sales Clicked', {
                    organizationName: selectedOrgName,
                    location: 'create team modal',
                  })
                }>
                <Button primary content="Contact sales" />
              </TargetBlank>
            ) : (
              <Button
                data-test="submit-create-team"
                primary
                disabled={isSubmitDisabled || loading}
                onClick={createTeamCallback}>
                Create team ➞
              </Button>
            )}
          </Modal.Actions>
        </Modal>
      );
    },
    {id: 'CreateTeamModalContent'}
  );

export const CreateTeamButton: FunctionComponent<CreateTeamButtonProps> =
  makeComp(
    props => {
      const [modalOpen, setModalOpen] = useState(false);

      const handleOpen = useCallback(() => setModalOpen(true), [setModalOpen]);
      const handleClose = useCallback(
        () => setModalOpen(false),
        [setModalOpen]
      );

      const viewer = useViewer();
      const userOrganizations = useUserOrganizationsQuery();

      // TODO(adrnswanberg | cvp): Replace this with organization limits.
      const isLocalAndTeamsAllowed = viewer?.limits.teams > 0;

      const orgOptions = useMemo(() => {
        if (viewer == null || userOrganizations.loading) {
          return [];
        }

        // Filter out the personal organizations
        const orgs =
          userOrganizations.data?.viewer?.organizations
            .filter(o => o.orgType !== OrgType.Personal)
            .map(o => ({
              text: o.name,
              value: o.id,
            })) ?? [];

        // For now, always make legacy teams an option in onprem.
        // It's only sometimes an option in local.
        const legacyTeamsOption =
          userFlags(viewer).teams_enabled ||
          envIsCloudOnprem ||
          (envIsLocal && isLocalAndTeamsAllowed)
            ? [{text: 'none', value: 'none'}]
            : [];
        return [...legacyTeamsOption, ...orgs];
      }, [
        viewer,
        userOrganizations.loading,
        userOrganizations.data,
        isLocalAndTeamsAllowed,
      ]);
      return (
        <>
          {props.renderButton?.(handleOpen) ?? (
            <Button
              data-test="create-team-button"
              className="create-team-button"
              size={props.size ?? 'tiny'}
              color={props.color}
              disabled={props.readonly === true}
              onClick={() => {
                window.analytics?.track('Create Team Started', {
                  location: 'create team modal',
                });
                handleOpen();
              }}>
              New team
            </Button>
          )}
          <CreateTeamModal
            {...props}
            orgOptions={orgOptions}
            open={modalOpen}
            onClose={handleClose}></CreateTeamModal>
        </>
      );
    },
    {id: 'CreateTeamButton'}
  );
